import { HNST_TEAM_KEYS, otherHnstTeam } from '../types/hnst';
import { getHnstRoundScoring, isHideSeekSettings, isHnstSettings } from '../types/round';
import { isHnsRoundPlayer, isHnsPlayerDisqualified, isFprRoundPlayer, isHnstPlayerOut, isHnstRoundPlayer } from '../types/player';
import { MARCO_POLO_GRACE_PERIOD_SECONDS } from '../constants/limits';
/**
 * Calculate the score (survival time in seconds) for a Hide and Seek (or Hide
 * and Seek Teams) round player
 * @param player - The round player to calculate score for
 * @param round - The round the player is in
 * @returns The score in seconds, or 0 if they started as a seeker or are disqualified
 */
export function calculateHnsPlayerRoundScore(player, round) {
    // Disqualified players get 0 points
    if (isHnsPlayerDisqualified(player)) {
        return 0;
    }
    // Use originalRole if available, otherwise fall back to role
    const playerOriginalRole = player.originalRole || player.role;
    // Seekers who started as seekers get 0 points
    if (playerOriginalRole === 'seeker') {
        return 0;
    }
    // Must have hide and seek settings
    if (!isHideSeekSettings(round.settings)) {
        return 0;
    }
    const { hidingDuration } = round.settings;
    const roundStartedAt = round.startedAt;
    if (!roundStartedAt) {
        return 0;
    }
    // Calculate when the seeking phase began
    const seekingStartedAt = roundStartedAt + (hidingDuration * 1000);
    // If player was captured, their score is time from seeking start to capture
    if (player.capturedAt) {
        const survivalMs = player.capturedAt - seekingStartedAt;
        const baseScore = Math.max(0, Math.floor(survivalMs / 1000));
        // Subtract Marco Polo penalty seconds
        const penaltySeconds = player.totalPenaltySeconds || 0;
        return Math.max(0, baseScore - penaltySeconds);
    }
    // If round is completed and player wasn't captured, they survived the whole round
    if (round.endedAt) {
        const survivalMs = round.endedAt - seekingStartedAt;
        const baseScore = Math.max(0, Math.floor(survivalMs / 1000));
        // Subtract Marco Polo penalty seconds
        const penaltySeconds = player.totalPenaltySeconds || 0;
        return Math.max(0, baseScore - penaltySeconds);
    }
    // Round still active - calculate current survival time
    const now = Date.now();
    const survivalMs = now - seekingStartedAt;
    const baseScore = Math.max(0, Math.floor(survivalMs / 1000));
    // Subtract Marco Polo penalty seconds
    const penaltySeconds = player.totalPenaltySeconds || 0;
    return Math.max(0, baseScore - penaltySeconds);
}
/**
 * Calculate final scores for all players across all completed Hide and Seek rounds
 */
export function calculateHnsGameFinalScores(game) {
    const playerScores = new Map();
    // Initialize scores for all players using playerId as the key
    for (const player of game.players) {
        playerScores.set(player.playerId, {
            playerId: player.playerId,
            socketId: player.socketId,
            characterName: player.characterName || `Player ${player.playerId.slice(-4)}`,
            totalScore: 0,
            roundsPlayed: 0,
            roundsAsHider: 0,
            timesCaptured: 0,
            timesDisqualified: 0,
        });
    }
    // Calculate scores from completed rounds (exclude disabled rounds)
    const completedRounds = game.rounds.filter(r => r.status === 'completed' && !r.disabled);
    for (const round of completedRounds) {
        for (const roundPlayer of round.players) {
            // Skip if not an HNS player
            if (!isHnsRoundPlayer(roundPlayer))
                continue;
            const playerScore = playerScores.get(roundPlayer.playerId);
            if (!playerScore)
                continue;
            playerScore.roundsPlayed++;
            // Track disqualifications
            if (isHnsPlayerDisqualified(roundPlayer)) {
                playerScore.timesDisqualified++;
            }
            // Use originalRole if available, otherwise fall back to role
            const playerOriginalRole = roundPlayer.originalRole || roundPlayer.role;
            // Only hiders accumulate score (disqualified players get 0 via calculateHnsPlayerRoundScore).
            // The score saved when the round ended is the one it was settled with.
            if (playerOriginalRole === 'hider') {
                playerScore.roundsAsHider++;
                playerScore.totalScore += roundPlayer.finalScoreSeconds ?? calculateHnsPlayerRoundScore(roundPlayer, round);
                if (roundPlayer.capturedAt) {
                    playerScore.timesCaptured++;
                }
            }
        }
    }
    // Sort by total score descending
    const leaderboard = Array.from(playerScores.values())
        .sort((a, b) => b.totalScore - a.totalScore);
    return {
        leaderboard,
        totalRounds: completedRounds.length,
    };
}
// ============================================================================
// Hide and Seek Teams (HNST) Scoring
// ============================================================================
/**
 * A Hide and Seek Teams round's result. The hiders' average time: every hider
 * counts in the team size; a disqualified one scores 0, and one never caught
 * scores to the end of the round (the scores saved when the round ended are
 * used, when there are). And each team's score by the round's scoring: see
 * HnstRoundScoring. Null for a round of another game type.
 */
export function calculateHnstRoundTeamScore(round, roundIndex) {
    if (!isHnstSettings(round.settings) || !round.settings.hidingTeam)
        return null;
    const { hidingTeam } = round.settings;
    const scoring = getHnstRoundScoring(round.settings);
    const hiders = round.players.filter(isHnstRoundPlayer).filter((p) => p.team === hidingTeam);
    const totalSeconds = hiders.reduce((sum, p) => sum + (p.finalScoreSeconds ?? calculateHnsPlayerRoundScore(p, round)), 0);
    const averageSeconds = hiders.length > 0 ? totalSeconds / hiders.length : 0;
    const found = hiders.filter((p) => p.capturedByPlayerId && !isHnsPlayerDisqualified(p)).length;
    const survived = hiders.filter((p) => !isHnstPlayerOut(p)).length;
    const seekingTeam = otherHnstTeam(hidingTeam);
    const teamSeconds = { [hidingTeam]: 0, [seekingTeam]: 0 };
    if (scoring.mode === 'points') {
        teamSeconds[seekingTeam] = found * scoring.secondsPerFind;
        if (scoring.survivorsScore)
            teamSeconds[hidingTeam] = survived * scoring.secondsPerFind;
    }
    else {
        teamSeconds[hidingTeam] = averageSeconds;
    }
    return {
        roundIndex,
        hidingTeam,
        scoring,
        teamSize: hiders.length,
        totalSeconds,
        averageSeconds,
        found,
        survived,
        teamSeconds,
    };
}
/**
 * A Hide and Seek Teams game's standings over its completed, enabled rounds:
 * each team's total is the sum of its round scores.
 */
export function calculateHnstStandings(game) {
    const teams = new Map(HNST_TEAM_KEYS.map((team) => [team, { team, totalSeconds: 0, roundsHidden: 0 }]));
    const players = new Map();
    const rounds = [];
    game.rounds.forEach((round, roundIndex) => {
        if (round.status !== 'completed' || round.disabled)
            return;
        const score = calculateHnstRoundTeamScore(round, roundIndex);
        if (!score)
            return;
        rounds.push(score);
        for (const team of teams.values())
            team.totalSeconds += score.teamSeconds[team.team];
        teams.get(score.hidingTeam).roundsHidden++;
        for (const roundPlayer of round.players) {
            if (!isHnstRoundPlayer(roundPlayer) || roundPlayer.team !== score.hidingTeam)
                continue;
            let standing = players.get(roundPlayer.playerId);
            if (!standing) {
                const player = game.players.find((p) => p.playerId === roundPlayer.playerId);
                standing = {
                    playerId: roundPlayer.playerId,
                    characterName: player?.characterName || roundPlayer.characterName || `Player ${roundPlayer.playerId.slice(-4)}`,
                    team: roundPlayer.team,
                    totalSeconds: 0,
                    roundsHidden: 0,
                    timesCaught: 0,
                    timesEliminated: 0,
                    timesDroppedOut: 0,
                    timesDisqualified: 0,
                };
                players.set(roundPlayer.playerId, standing);
            }
            standing.roundsHidden++;
            standing.totalSeconds += roundPlayer.finalScoreSeconds ?? calculateHnsPlayerRoundScore(roundPlayer, round);
            if (isHnsPlayerDisqualified(roundPlayer))
                standing.timesDisqualified++;
            else if (roundPlayer.capturedByPlayerId)
                standing.timesCaught++;
            else if (roundPlayer.capturedAt && roundPlayer.eliminatedByDeaths)
                standing.timesEliminated++;
            else if (roundPlayer.capturedAt)
                standing.timesDroppedOut++;
        }
    });
    const teamList = [...teams.values()].sort((a, b) => b.totalSeconds - a.totalSeconds);
    const leader = teamList[0].totalSeconds === teamList[1].totalSeconds ? null : teamList[0].team;
    return {
        teams: teamList,
        leader,
        rounds,
        players: [...players.values()].sort((a, b) => b.totalSeconds - a.totalSeconds),
        totalRounds: rounds.length,
    };
}
/**
 * Calculate penalty seconds for a Marco Polo response (HNS specific)
 */
export function calculateMarcoPoloPenalty(activatedAt, respondedAt, gracePeriodSeconds = MARCO_POLO_GRACE_PERIOD_SECONDS) {
    const responseTimeMs = respondedAt - activatedAt;
    const responseTimeSeconds = responseTimeMs / 1000;
    const penaltySeconds = Math.max(0, responseTimeSeconds - gracePeriodSeconds);
    return Math.floor(penaltySeconds);
}
/**
 * Get the current accumulated penalty for a non-responding player (HNS Marco Polo)
 */
export function getCurrentPenaltySeconds(activatedAt, gracePeriodSeconds = MARCO_POLO_GRACE_PERIOD_SECONDS) {
    const now = Date.now();
    const elapsedSeconds = (now - activatedAt) / 1000;
    return Math.max(0, Math.floor(elapsedSeconds - gracePeriodSeconds));
}
// ============================================================================
// FP Race (FPR) Scoring
// ============================================================================
/**
 * Calculate per-round team results from FprRoundPlayer data.
 * Teams are ranked by finish time (ascending); DNF teams receive 0 points.
 * Points formula: (N - rank + 1)² where N = total number of teams.
 */
export function calculateFprRoundResults(round, teams) {
    const fprPlayers = round.players.filter(isFprRoundPlayer);
    const teamPlayerMap = new Map();
    for (const player of fprPlayers) {
        if (!player.teamId)
            continue;
        const existing = teamPlayerMap.get(player.teamId) ?? [];
        existing.push(player);
        teamPlayerMap.set(player.teamId, existing);
    }
    const N = teams.length;
    const intermediates = teams.map((team) => {
        const members = teamPlayerMap.get(team.id) ?? [];
        const isDnf = members.some((m) => m.dnf) || members.every((m) => !m.completedAt);
        const totalDeaths = members.reduce((sum, m) => sum + (m.deaths ?? 0), 0);
        const completedAts = members.map((m) => m.completedAt).filter((t) => t !== undefined);
        const completedAt = completedAts.length > 0 ? Math.max(...completedAts) : undefined;
        return { teamId: team.id, teamName: team.name, totalDeaths, completedAt, isDnf };
    });
    const finished = intermediates
        .filter((t) => !t.isDnf && t.completedAt !== undefined)
        .sort((a, b) => (a.completedAt ?? 0) - (b.completedAt ?? 0));
    const dnf = intermediates.filter((t) => t.isDnf || t.completedAt === undefined);
    const sorted = [...finished, ...dnf];
    return sorted.map((t, idx) => {
        const isFinished = !t.isDnf && t.completedAt !== undefined;
        const rank = idx + 1;
        return {
            teamId: t.teamId,
            teamName: t.teamName,
            placement: isFinished ? rank : 0,
            roundPoints: isFinished ? Math.pow(N - rank + 1, 2) : 0,
            totalDeaths: t.totalDeaths,
            completedAt: t.completedAt,
        };
    });
}
/**
 * Calculate cumulative team scores across all completed FP Race rounds.
 */
export function calculateFprGameFinalScores(game) {
    const teams = game.fprTeams ?? [];
    const teamScores = new Map();
    for (const team of teams) {
        teamScores.set(team.id, {
            teamId: team.id,
            teamName: team.name,
            totalScore: 0,
            roundsPlayed: 0,
        });
    }
    const completedRounds = game.rounds.filter((r) => r.status === 'completed' && !r.disabled);
    for (const round of completedRounds) {
        const results = calculateFprRoundResults(round, teams);
        for (const result of results) {
            const score = teamScores.get(result.teamId);
            if (!score)
                continue;
            score.totalScore += result.roundPoints;
            score.roundsPlayed++;
        }
    }
    const leaderboard = Array.from(teamScores.values()).sort((a, b) => b.totalScore - a.totalScore);
    return { leaderboard, totalRounds: completedRounds.length };
}
// ============================================================================
// Trailblazers (TRL) Scoring
// ============================================================================
/**
 * Sliding score for a Trailblazers capture based on capture order.
 * The first team to capture (rank 1) receives `basePoints`; each subsequent
 * capturing team receives `decrement` fewer points, floored at `min`.
 *
 * @param rank 1-indexed capture order among teams
 * @param basePoints points for the first team (default 1000)
 * @param decrement points removed per rank (default 100)
 * @param min minimum points awarded for any capture (default 100)
 */
export function computeTrlSlidingScore(rank, basePoints = 1000, decrement = 100, min = 100) {
    const points = basePoints - (rank - 1) * decrement;
    return Math.max(min, points);
}
